import { ChangeEvent } from 'react';
import { Post } from 'lib/types';
import {
  Alert,
  AlertTitle,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { MetricProps } from 'components/PostMetrics/types';
import { getAllowedMetrics } from 'components/PostMetrics/helpers';
import { PostRow } from './PostRow';
import { PostRowSkeleton } from './PostRowSkeleton';
import { Method } from './AddDrawer';

interface Props {
  posts: Post[];
  selectedPostIds: number[];
  isLoading: boolean;
  error?: Error;
  drawerOpen?: boolean;
  method?: Method;
  onChange: (postIds: number[]) => void;
  onCopy: (post: Post) => void;
  onSelectMatch?: (matchId: string) => void;
}

export const FilterPostResult = ({
  posts,
  selectedPostIds,
  isLoading,
  error,
  drawerOpen = false,
  method,
  onChange,
  onCopy,
  onSelectMatch,
}: Props) => {
  if (isLoading && !posts?.length) {
    return (<div className="mt-4"><PostRowSkeleton /></div>);
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        Something went wrong fetching data. Please double check your filters!
      </Alert>
    );
  }

  if (!isLoading && (!posts || posts.length <= 0)) {
    return (<div className="text-xl min-h-screen font-semibold text-center">No posts found for the selected filters</div>);
  }

  const onChangeAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onChange(posts.map((p) => p.id));
      return;
    }
    onChange([]);
  };

  const onChangeEach = (postId: number, matchId?: string) => {
    if (matchId) {
      onSelectMatch?.(matchId);
    }
    if (postId && !selectedPostIds.includes(postId)) {
      onChange(selectedPostIds.concat(postId));
      return;
    }
    onChange(selectedPostIds.filter((s) => s !== postId));
  };

  // Determine which metrics are active (have at least one non-null value)
  const getActiveMetrics = () => {
    const activeMetrics = new Set<MetricProps>();

    posts.forEach((post) => {
      const allowedMetrics = getAllowedMetrics(post.network, false);
      allowedMetrics.forEach((metric) => {
        const value = post[metric.field] as number;
        if (value || metric.renderIfNull) {
          activeMetrics.add(metric);
        }
      });
    });

    return Array.from(activeMetrics);
  };

  const activeMetrics = getActiveMetrics();

  // Mobile view (cards)
  const mobileView = (
    <div className="space-y-4 md:hidden">
      {posts.map((post) => (
        <div key={post.id} className="border rounded-lg p-4 space-y-3">
          <PostRow
            post={post}
            selected={selectedPostIds.includes(post.id)}
            onChange={onChangeEach}
            drawerOpen={drawerOpen}
            method={method}
            onCopy={() => onCopy(post)}
            isMobile
          />
        </div>
      ))}
    </div>
  );

  // Desktop view (table)
  const desktopView = (
    <div className="hidden md:block">
      <table className="table-auto w-full border rounded-md text-sm">
        <thead>
          <tr className="bg-hintgray">
            <th className="py-2 px-2 text-left">
              <Checkbox
                indeterminate={selectedPostIds.length > 0 && selectedPostIds.length < posts.length}
                checked={selectedPostIds.length === posts.length}
                onChange={onChangeAll}
              />
              Media
            </th>
            <th className="py-2 px-2 text-left">Post Data</th>
            {activeMetrics.map((metric) => (
              <th key={metric.field} className="py-2 pl-2 text-left border-l border-lightgray">
                <Tooltip title={metric.label}>
                  <div className="flex items-center">
                    {metric.icon}
                  </div>
                </Tooltip>
              </th>
            ))}
            <th className="py-2 px-2 text-left border-l border-lightgray">Credits</th>
            {!drawerOpen && <th className="py-2 px-2 text-left">Caption</th>}
            <th className="py-2 px-2 text-left">Tags and Events</th>
            <th className="py-2 px-2 text-left">Projects</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((post) => (
            <tr key={post.id} className="border-t border-lightgray hover:bg-hintgray">
              <PostRow
                post={post}
                selected={selectedPostIds.includes(post.id)}
                onChange={onChangeEach}
                drawerOpen={drawerOpen}
                method={method}
                onCopy={() => onCopy(post)}
                activeMetrics={activeMetrics}
              />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {mobileView}
      {desktopView}
    </>
  );
};
